"use client";

import Link from "next/link";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { apiFetch, apiUrl } from "@/lib/api";
import { CATEGORY_GROUPS } from "@/lib/categories";

type Deal = {
  id: number;
  item_id: string;
  title: string | null;
  description_user: string | null;
  category: { id: number; slug: string; name: string } | null;
  price_current_cents: number | null;
  price_original_cents: number | null;
  discount_pct: number | null;
  currency: string;
  images: string[] | null;
  upvotes: number;
  downvotes: number;
  comments_count: number;
  // opcional, si el API lo expone
  views?: number;
  views_count?: number;
  metrics?: { views?: number };
  out_url: string;
};

type DealsResponse = {
  items: Deal[];
  page: number;
  total: number;
};

type MetricsResponse = {
  range: string;
  items: { deal_id: number; views: number; clicks: number }[];
};

const fmtPrice = (cents: number | null, currency: string) => {
  if (cents == null) return "";
  const cur = currency && /^[A-Z]{3}$/.test(currency) ? currency : "ARS";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: cur,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(cents / 100);
};

function HomeInner() {
  const router = useRouter();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState<"hot" | "new" | "discount">("hot");
  const ALL = "all";
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [categoriesCsv, setCategoriesCsv] = useState<string | undefined>(undefined);
  const [categoryGroup, setCategoryGroup] = useState<string>(ALL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popular, setPopular] = useState<Deal[]>([]);
  const [highlights, setHighlights] = useState<Deal[]>([]);
  const [userVotes, setUserVotes] = useState<Record<number, 1 | -1 | 0>>({});
  const [me, setMe] = useState<{ display_name?: string | null; avatar_url?: string | null } | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [viewsMap, setViewsMap] = useState<Record<number, number>>({});

  const getViews = (deal: Deal) => deal.views ?? deal.views_count ?? deal.metrics?.views ?? null;

  const fetchViews = async (ids: number[]) => {
    if (!ids.length) return;
    try {
      const params = new URLSearchParams({ range: "today", ids: ids.join(",") });
      const res = (await apiFetch(`/api/v1/metrics/deals?${params.toString()}`)) as MetricsResponse;
      const map: Record<number, number> = {};
      res.items?.forEach((item) => {
        if (item?.deal_id != null) map[item.deal_id] = item.views ?? 0;
      });
      setViewsMap((prev) => ({ ...prev, ...map }));
    } catch {
      /* silencioso: vistas no bloquean UI */
    }
  };

  const nextCategory = (selected: string, clicked: string) => {
    if (clicked === ALL) return { selected: undefined, group: ALL, csv: undefined };
    if (selected === clicked) return { selected: undefined, group: ALL, csv: undefined };
    return { selected: clicked, group: clicked, csv: undefined };
  };

  const load = async (nextPage: number, append: boolean) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sort,
        page: String(nextPage),
        page_size: "18"
      });
      if (category) params.set("category", category);
      if (!category && categoriesCsv) params.set("categories", categoriesCsv);
      const res = (await apiFetch(`/api/v1/deals?${params.toString()}`)) as DealsResponse;
      const items = res.items || [];
      setDeals((prev) => (append ? [...prev, ...items] : items));
      if (!append) {
        setViewsMap({});
      }
      fetchViews(items.map((d) => d.id));
      setPage(res.page || nextPage);
      setTotal(res.total || 0);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Error cargando ofertas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, category, categoriesCsv]);

  useEffect(() => {
    const fetchPopular = async () => {
      try {
        const res = (await apiFetch("/api/v1/deals?sort=hot&page=1&page_size=6")) as DealsResponse;
        setPopular(res.items || []);
      } catch {
        setPopular([]);
      }
    };
    const fetchHighlights = async () => {
      try {
        const res = (await apiFetch("/api/v1/deals/highlights?limit=8")) as { items: Deal[] };
        setHighlights(res.items || []);
      } catch {
        setHighlights([]);
      }
    };
    fetchPopular();
    fetchHighlights();
    const fetchMe = async () => {
      try {
        const m = await apiFetch("/api/v1/me");
        setMe(m as any);
      } catch {
        setMe(null);
      }
    };
    fetchMe();
  }, []);

  const spotlight = useMemo(() => highlights, [highlights]);
  const gridDeals = useMemo(() => deals, [deals]);

  const adEvery = 8;
  type GridItem = { type: "deal"; deal: Deal } | { type: "ad"; key: string };
  const gridItems: GridItem[] = useMemo(() => {
    const items: GridItem[] = [];
    let adCount = 0;
    gridDeals.forEach((deal, idx) => {
      items.push({ type: "deal", deal });
      if ((idx + 1) % adEvery === 0) {
        adCount += 1;
        items.push({ type: "ad", key: `ad-${adCount}` });
      }
    });
    return items;
  }, [gridDeals]);

  const renderDealCard = (deal: Deal, index: number) => {
    const img = deal.images?.[0] || deal.images?.[1];
    const price = fmtPrice(deal.price_current_cents, deal.currency);
    const original = fmtPrice(deal.price_original_cents, deal.currency);
    const votes = deal.upvotes - deal.downvotes;
    const views = viewsMap[deal.id] ?? getViews(deal) ?? 0;
    const currentVote = userVotes[deal.id] ?? 0;

    const handleVote = async (value: 1 | -1) => {
      try {
        if (currentVote === value) {
          await apiFetch(`/api/v1/deals/${deal.id}/vote`, { method: "DELETE" });
          setUserVotes((prev) => ({ ...prev, [deal.id]: 0 }));
          setDeals((prev) =>
            prev.map((d) =>
              d.id === deal.id
                ? { ...d, upvotes: d.upvotes - (currentVote === 1 ? 1 : 0), downvotes: d.downvotes - (currentVote === -1 ? 1 : 0) }
                : d
            )
          );
          return;
        }
        const res: any = await apiFetch(`/api/v1/deals/${deal.id}/vote`, { method: "POST", body: { value } });
        setUserVotes((prev) => ({ ...prev, [deal.id]: value }));
        setDeals((prev) =>
          prev.map((d) =>
            d.id === deal.id
              ? {
                  ...d,
                  upvotes:
                    res?.upvotes ?? d.upvotes + (value === 1 ? 1 : 0) + (currentVote === 1 ? -1 : 0) + (currentVote === -1 && value === 1 ? 1 : 0),
                  downvotes:
                    res?.downvotes ??
                    d.downvotes + (value === -1 ? 1 : 0) + (currentVote === -1 ? -1 : 0) + (currentVote === 1 && value === -1 ? 1 : 0)
                }
              : d
          )
        );
      } catch (e: any) {
        if (e?.status === 401) {
          router.push(`/login?next=/`);
          return;
        }
        setError(e?.message || "No se pudo votar");
      }
    };
    return (
      <article
        key={deal.id}
        className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-mist bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
        onClick={() => router.push(`/deals/${deal.id}`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            router.push(`/deals/${deal.id}`);
          }
        }}
      >
        <div className="absolute left-4 top-4 flex items-center gap-2">
          <span className="whitespace-nowrap rounded-full bg-accent px-3 py-1 text-[11px] font-bold uppercase text-midnight shadow">
            {deal.discount_pct != null ? `${deal.discount_pct}% OFF` : "Oferta"}
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-3 p-4 pt-10 pb-4">
          <div className="aspect-[4/3] overflow-hidden rounded-xl bg-mist flex items-center justify-center">
            {img ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={img} alt={deal.title || ""} className="h-full w-full object-contain bg-white" />
            ) : null}
          </div>
          {index === 2 ? (
            <div className="rounded-xl bg-accent px-3 py-2 text-xs font-semibold text-midnight shadow-sm text-center">
              Anuncio: oferta destacada
            </div>
          ) : null}
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-accentStrong">
                {deal.category?.name || "Categoría"}
              </div>
              <h3 className="text-base font-semibold leading-tight text-ink line-clamp-2">
                {deal.title || deal.item_id}
              </h3>
            </div>
            <div className="flex flex-col items-end text-right">
              <div className="text-2xl font-black text-accentStrong break-words text-right max-w-[140px]">
                {price || "—"}
              </div>
              {original ? <div className="text-sm text-charcoal/50 line-through">{original}</div> : null}
            </div>
          </div>
          <div className="grid w-full grid-cols-4 gap-2 text-sm text-slate-600">
            <span className="col-span-2 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-accentMuted px-3 font-semibold text-ink">
              <button
                aria-label="Upvote"
                className={`flex h-5 w-5 items-center justify-center rounded-full border border-accent text-accent transition hover:bg-accent hover:text-midnight ${
                  currentVote === 1 ? "bg-accent text-midnight" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleVote(1);
                }}
              >
                ▲
              </button>
              <span className="font-bold leading-none">{votes}</span>
              <button
                aria-label="Downvote"
                className={`flex h-5 w-5 items-center justify-center rounded-full border border-accent text-accent transition hover:bg-accent hover:text-midnight ${
                  currentVote === -1 ? "bg-accent text-midnight" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleVote(-1);
                }}
                >
                  ▼
                </button>
              </span>
            <span className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-mist px-3 text-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 8.5c0-1.933 1.79-3.5 4-3.5h4c2.21 0 4 1.567 4 3.5v4c0 1.933-1.79 3.5-4 3.5H9.5L6 18.5V8.5Z"
                />
              </svg>
              <span className="font-semibold">{deal.comments_count}</span>
            </span>
            <span className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-mist px-3 text-[11px] text-slate-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <circle cx="12" cy="12" r="8" />
                <circle cx="12" cy="12" r="2" />
              </svg>
              {views !== null ? views : "—"}
            </span>
          </div>
          <div className="pt-3">
            <a
              href={apiUrl(deal.out_url)}
              target="_blank"
              rel="noreferrer"
              className="block w-full rounded-full border border-accent px-4 py-2 text-center text-sm font-semibold text-accent transition hover:bg-accent hover:text-midnight whitespace-nowrap"
              onClick={(e) => e.stopPropagation()}
            >
              Ver oferta
            </a>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 border-b border-accent/50 bg-accentStrong text-white shadow-lg">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="text-xl font-semibold tracking-tight">ofertashoy</div>
          <div className="flex flex-1 items-center justify-center px-6" />
          <div className="flex items-center gap-3 text-sm">
            <Link
              href="/post"
              className="rounded-full bg-[#fbbf24] px-5 py-2.5 font-bold text-ink shadow-lg shadow-black/20 transition hover:shadow-xl hover:brightness-110"
            >
              Publicar oferta
            </Link>
            {me ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu((v) => !v)}
                  className="flex h-10 w-10 overflow-hidden items-center justify-center rounded-full border border-white/60 bg-white/90 text-ink shadow-sm"
                >
                  {me.avatar_url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={me.avatar_url} alt="avatar" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-xs font-semibold">{me.display_name?.[0]?.toUpperCase() || "U"}</span>
                  )}
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-36 rounded-xl border border-mist bg-white p-2 text-sm text-ink shadow-lg">
                    <Link
                      href="/me"
                      className="block w-full rounded-lg px-3 py-2 text-left hover:bg-mist"
                      onClick={() => setShowUserMenu(false)}
                    >
                      Mis publicaciones
                    </Link>
                    <button
                      onClick={async () => {
                        await apiFetch("/api/v1/auth/logout", { method: "POST" });
                        setMe(null);
                        setShowUserMenu(false);
                        router.refresh();
                      }}
                      className="w-full rounded-lg px-3 py-2 text-left hover:bg-mist"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                href="/login"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-white/90 text-ink shadow-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 12c2.761 0 5-2.015 5-4.5S14.761 3 12 3 7 5.015 7 7.5 9.239 12 12 12Zm0 2c-3.315 0-6 2.239-6 5v.5h12V19c0-2.761-2.685-5-6-5Z"
                  />
                </svg>
              </Link>
            )}
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl items-center gap-3 overflow-x-auto px-4 pb-3 text-sm text-white">
        {CATEGORY_GROUPS.map((group) => {
          const isActive = categoryGroup === group.key;
          return (
            <button
              key={group.key}
              onClick={() => {
                const clicked = group.key;
                let nextGroup = categoryGroup;
                let nextCat = category;
                let nextCsv = categoriesCsv;

                if (clicked === ALL) {
                  nextGroup = ALL;
                  nextCat = undefined;
                  nextCsv = undefined;
                } else if (categoryGroup === clicked) {
                  // toggle back to ALL
                  nextGroup = ALL;
                  nextCat = undefined;
                  nextCsv = undefined;
                } else {
                  nextGroup = clicked;
                  if (group.slugs.length === 1) {
                    nextCat = group.slugs[0];
                    nextCsv = undefined;
                  } else if (group.slugs.length > 1) {
                    nextCat = undefined;
                    nextCsv = group.slugs.join(",");
                  } else {
                    nextCat = undefined;
                    nextCsv = undefined;
                  }
                }

                // If nothing changes, do nothing (prevents wiping the feed on double-click Todos)
                if (
                  nextGroup === categoryGroup &&
                  nextCat === category &&
                  nextCsv === categoriesCsv
                ) {
                  return;
                }

                setCategoryGroup(nextGroup);
                setCategory(nextCat);
                setCategoriesCsv(nextCsv);
                setPage(1);
                setDeals([]);
              }}
              className={`rounded-full border border-white/50 bg-white/20 px-3 py-1 transition hover:bg-white hover:text-ink ${
                isActive ? "bg-white text-ink" : ""
              }`}
            >
              {group.label}
            </button>
          );
        })}
      </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        <div className="mb-4 flex items-center gap-2">
          {(["hot", "new", "discount"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setSort(tab);
                setPage(1);
              }}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${
                sort === tab ? "bg-accent text-midnight shadow" : "bg-mist text-ink"
              }`}
            >
              {tab === "hot" ? "Hot" : tab === "new" ? "Nuevos" : "Descuento"}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-8">
          <section className="w-full overflow-hidden rounded-2xl border border-mist bg-white p-4 shadow-lg">
            <div className="mb-3 flex items-center justify-between text-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-charcoal/60">Ofertas del dia</div>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {spotlight.map((deal) => {
                const img = deal.images?.[0] || deal.images?.[1];
                const price = fmtPrice(deal.price_current_cents, deal.currency);
                const original = fmtPrice(deal.price_original_cents, deal.currency);
                const views = viewsMap[deal.id] ?? getViews(deal) ?? 0;
                return (
                  <div
                    key={deal.id}
                    className="relative flex w-[260px] sm:w-[280px] flex-none flex-col justify-between rounded-xl border border-mist bg-white p-3 shadow-sm"
                    onClick={() => router.push(`/deals/${deal.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        router.push(`/deals/${deal.id}`);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-accentStrong">
                          {deal.category?.name || "Mercado Libre"}
                        </p>
                        <p className="text-sm font-semibold leading-tight text-charcoal line-clamp-2">
                          {deal.title || deal.item_id}
                        </p>
                      </div>
                      {deal.discount_pct != null ? (
                        <span className="whitespace-nowrap rounded-full bg-accent px-2 py-1 text-[11px] font-bold uppercase text-midnight shadow-sm">
                          {deal.discount_pct}% OFF
                        </span>
                      ) : null}
                    </div>
                <div className="mt-3 aspect-[4/3] overflow-hidden rounded-xl bg-mist flex items-center justify-center">
                  {img ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={img} alt={deal.title || ""} className="h-full w-full object-contain bg-white" />
                  ) : null}
                </div>
                <div className="mt-2 flex items-end justify-between">
                  <div>
                    <div className="text-2xl font-black text-accentStrong break-words max-w-[140px]">{price}</div>
                    {original ? <div className="text-xs text-charcoal/50 line-through">{original}</div> : null}
                  </div>
                </div>
                <div className="mt-2 grid w-full grid-cols-4 gap-2 text-sm text-slate-600">
                  <span className="col-span-2 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-accentMuted px-3 font-semibold text-ink">
                    ▲ {deal.upvotes - deal.downvotes}
                  </span>
                  <span className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-mist px-3 text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 8.5c0-1.933 1.79-3.5 4-3.5h4c2.21 0 4 1.567 4 3.5v4c0 1.933-1.79 3.5-4 3.5H9.5L6 18.5V8.5Z" />
                    </svg>
                    <span className="font-semibold">{deal.comments_count}</span>
                  </span>
                  <span className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-mist px-3 text-[11px] text-slate-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <circle cx="12" cy="12" r="8" />
                      <circle cx="12" cy="12" r="2" />
                    </svg>
                    {views !== null ? views : "—"}
                  </span>
                </div>
                <div className="pt-3">
                  <Link
                    href={apiUrl(deal.out_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="block w-full rounded-full border border-accent px-4 py-2 text-center text-sm font-semibold text-accent transition hover:bg-accent hover:text-midnight whitespace-nowrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Ver oferta
                  </Link>
                </div>
              </div>
            );
          })}
              {spotlight.length === 0 && <div className="text-sm text-slate-500">No hay ofertas cargadas el dia de hoy aun, se el primero en publicar una.</div>}
            </div>
          </section>

          <div className="flex flex-col gap-6 lg:flex-row">
            <section className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="hidden rounded-2xl border border-mist bg-white p-4 text-sm text-slate-700 shadow-sm md:block">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Slot publicitario</div>
                <p className="font-semibold">Tu anuncio destacado aqui.</p>
                <p className="text-slate-600">Espacio para anuncios integrados en el feed.</p>
              </div>
              {gridItems.map((item, index) =>
                item.type === "deal" ? (
                  <div key={item.deal.id}>{renderDealCard(item.deal, index)}</div>
                ) : (
                  <div
                    key={item.key}
                    className="flex h-full flex-col justify-between rounded-2xl border border-dashed border-accent/40 bg-accentMuted p-4 text-sm text-ink shadow-sm"
                  >
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-accentStrong">Publicidad</div>
                      <p className="font-semibold">Bloque de anuncio.</p>
                    </div>
                    <p className="text-slate-600">Espacio para anuncios integrados en el feed.</p>
                  </div>
                )
              )}
              {gridDeals.length === 0 && !loading && (
                <div className="col-span-full rounded-2xl border border-slate-200 bg-white p-6 text-center text-slate-500 shadow-sm">
                  No encontramos resultados.
                </div>
              )}
            </section>

            <aside className="w-full flex-none space-y-3 lg:w-72">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-slate-700">Populares hoy</h4>
                <div className="mt-3 space-y-3">
                  {popular.map((deal) => (
                    <div key={deal.id} className="flex items-start gap-3">
                      <span className="flex h-9 min-w-[56px] items-center justify-center rounded-full bg-accentMuted px-2 text-xs font-bold text-charcoal">
                        {deal.upvotes - deal.downvotes}
                      </span>
                      <div>
                        <Link href={`/deals/${deal.id}`} className="text-sm font-semibold leading-tight hover:text-accentStrong">
                          {deal.title || deal.item_id}
                        </Link>
                        <p className="flex items-center gap-1 text-xs text-slate-500">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 8.5c0-1.933 1.79-3.5 4-3.5h4c2.21 0 4 1.567 4 3.5v4c0 1.933-1.79 3.5-4 3.5H9.5L6 18.5V8.5Z"
                            />
                          </svg>
                          {deal.comments_count}
                        </p>
                      </div>
                    </div>
                  ))}
                  {popular.length === 0 && <p className="text-xs text-slate-500">Sin datos</p>}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Estado</div>
                <p className="font-semibold">MVP en construcción</p>
                <p className="text-slate-600">Feed conectado a API. Diseño original preservado.</p>
              </div>
            </aside>
          </div>

          <div className="mt-10 mb-16 flex items-center justify-center">
            {deals.length < total ? (
              <button
                disabled={loading}
                onClick={() => load(page + 1, true)}
                className="rounded-full bg-ink px-5 py-2 text-sm font-semibold text-white shadow hover:bg-charcoal disabled:opacity-60"
              >
                {loading ? "Cargando..." : "Cargar más"}
              </button>
            ) : (
              <p className="text-sm text-slate-500">Fin de resultados.</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default function Home() {
  return (
    <Suspense fallback={<div className="p-4">Cargando...</div>}>
      <HomeInner />
    </Suspense>
  );
}
